import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MainLayout } from '@/components/layouts'
import { MainContent } from '@/components/content'
import { SettingSidebar } from '@/components/sidebar'
import { LoadingScreen } from '@/components/common'
import { ChevronRight } from '@/components/icons'
import { toast } from 'react-hot-toast'
import {
  usePartnerConnections,
  useUpdatePartnerPermissions,
  useRevokePartnerPermissions,
  PartnerPermissionsResponse,
} from '@/services'
import { ConsentPermissionDescriptor } from '@/types'
import {
  buildGrants,
  buildPermissionsPayload,
  isRequiredPermission,
  permissionLabel,
  resolveExistingGrants,
  visiblePermissions,
  PermissionLabelLocalizer,
} from '@/pages/external/consentPermissions'

function Toggle({ checked, disabled, onChange }: { checked: boolean; disabled?: boolean; onChange?: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        onChange?.()
      }}
      className={[
        'relative inline-flex h-[22px] w-[40px] flex-shrink-0 items-center rounded-full transition-all duration-200',
        disabled
          ? 'bg-navy-900/15 cursor-not-allowed'
          : checked
            ? 'bg-green cursor-pointer hover:shadow-[0_0_0_3px_rgba(0,200,116,0.15)]'
            : 'bg-navy-900/10 cursor-pointer hover:bg-navy-900/20',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-4 w-4 rounded-full shadow-sm transition-all duration-200',
          disabled
            ? 'bg-white/80 translate-x-[19px]'
            : checked
              ? 'bg-white translate-x-[19px]'
              : 'bg-white translate-x-[3px]',
        ].join(' ')}
      />
    </button>
  )
}

export default function ConnectionsPage() {
  const { data: partners, isLoading } = usePartnerConnections()

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <MainLayout>
      <SettingSidebar />
      <MainContent>
        <div className="mx-auto w-full max-w-2xl">
          <div className="mb-6">
            <h1 className="text-[22px] font-bold text-white tracking-tight leading-tight">Connected Apps</h1>
            <p className="mt-1 text-[13px] text-white/50 leading-relaxed">
              Manage which platforms can access your DUPR data.
            </p>
          </div>

          {!partners || partners.length === 0 ? (
            <div className="bg-white/[0.04] border border-white/[0.08] py-12 px-6 text-center">
              <p className="text-white/40 text-sm font-medium">No connected apps</p>
              <p className="text-white/25 text-xs mt-1">Authorized platforms will appear here.</p>
            </div>
          ) : (
            <div className="flex flex-col">
              {partners.map((partner) => (
                <PartnerCard key={partner.partnerId} partner={partner} />
              ))}
            </div>
          )}
        </div>
      </MainContent>
    </MainLayout>
  )
}

/** What the partner can currently see, for the collapsed header. Required keys always count. */
function buildSharedSummary(
  descriptors: ConsentPermissionDescriptor[],
  grants: Record<string, boolean>,
  localizer: PermissionLabelLocalizer
): string {
  const shared = descriptors
    .filter((d) => isRequiredPermission(d) || grants[d.key])
    .map((d) => permissionLabel(d, localizer))
  return shared.length > 0 ? shared.join(', ') : 'None'
}

/** An error's message, whether it is an axios rejection or a `status: FAILURE` turned into one. */
const errorMessage = (e: any, fallback: string): string => e?.response?.data?.message || e?.message || fallback

function PartnerCard({ partner }: { partner: PartnerPermissionsResponse }) {
  const { i18n } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [confirmingRevoke, setConfirmingRevoke] = useState(false)

  // Every descriptor the server sent for this partner, in its order. Rows are keyed by the server's
  // keys, so a consent this build has never heard of still shows and can still be switched.
  const visibleDescriptors = visiblePermissions(partner.permissionDescriptors)
  // What is granted now: the server's `grants` map, or the legacy fixed-field object on an older server.
  const savedGrants = resolveExistingGrants(partner.grants, partner.permissions)
  const [toggles, setToggles] = useState<Record<string, boolean>>(() => ({ ...savedGrants }))

  const { mutate: updatePermissions, isLoading: isSaving } = useUpdatePartnerPermissions()
  const { mutate: revokePermissions, isLoading: isRevoking } = useRevokePartnerPermissions()

  const onToggle = (key: string) => {
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const optionalDescriptors = visibleDescriptors.filter((d) => !isRequiredPermission(d))
  const turnedOn = optionalDescriptors.filter((d) => !!toggles[d.key] && !savedGrants[d.key]).map((d) => d.key)
  const turnedOff = optionalDescriptors.filter((d) => !toggles[d.key] && !!savedGrants[d.key]).map((d) => d.key)
  const hasChanges = turnedOn.length > 0 || turnedOff.length > 0

  /**
   * Saving is two calls, because granting is additive on the server: a POST switches keys on but
   * never off, so the keys switched off go through the revoke endpoint by name. Only optional keys
   * ever land in the revoke — the server refuses a required one, and the rows for those are locked.
   */
  const onSave = async () => {
    const saves: Promise<unknown>[] = []
    if (turnedOn.length > 0) {
      const grants = buildGrants(visibleDescriptors, toggles)
      saves.push(
        new Promise((resolve, reject) =>
          updatePermissions(
            { partnerId: partner.partnerId, grants, permissions: buildPermissionsPayload(grants) },
            { onSuccess: resolve, onError: reject }
          )
        )
      )
    }
    if (turnedOff.length > 0) {
      const revoked = Object.fromEntries(turnedOff.map((key) => [key, true]))
      saves.push(
        new Promise((resolve, reject) =>
          revokePermissions(
            { partnerId: partner.partnerId, keys: turnedOff, permissions: buildPermissionsPayload(revoked) },
            { onSuccess: resolve, onError: reject }
          )
        )
      )
    }
    try {
      await Promise.all(saves)
      toast.success('Permissions saved')
    } catch (e: any) {
      toast.error(errorMessage(e, 'Failed to update'))
    }
  }

  const onRevoke = () => {
    revokePermissions(
      { partnerId: partner.partnerId },
      {
        onSuccess: () => {
          toast.success(`Access revoked for ${partner.partnerName}`)
          setConfirmingRevoke(false)
        },
        onError: (e: any) => {
          toast.error(errorMessage(e, 'Failed to revoke'))
          setConfirmingRevoke(false)
        },
      }
    )
  }

  const sharedSummary = buildSharedSummary(visibleDescriptors, savedGrants, i18n)
  const asksForMore = (partner.missingRequired?.length ?? 0) > 0

  return (
    <div className="bg-white border-b border-gray-200 first:border-t">
      {/* Accordion header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-4 py-3 flex items-center gap-x-3 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="w-7 h-7 rounded-full bg-navy-900/[0.06] flex items-center justify-center flex-shrink-0">
          <span className="text-[11px] font-bold text-navy-900/50">{partner.partnerName.charAt(0).toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[13px] font-semibold text-navy-900">{partner.partnerName}</span>
          <span className="text-[11px] text-gray-500 ml-3 truncate inline-block max-w-[180px] md:max-w-[320px] align-middle">
            Sharing: {sharedSummary}
          </span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-gray-300 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
        />
      </button>

      {/* Accordion body */}
      <div
        className={`transition-all duration-200 overflow-hidden ${
          expanded ? 'max-h-[1200px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {/* The partner started requiring something this member never granted. It cannot be granted
            from here in a way the partner would notice until the next connect, so say what happens. */}
        {asksForMore && (
          <p className="border-t border-gray-100 bg-amber-50 px-4 py-2 text-[11px] text-amber-900">
            {partner.partnerName} now asks for more than you've shared. You'll be asked next time you connect.
          </p>
        )}

        {/* Permission rows — one per server descriptor, in the server's order */}
        <div className="border-t border-gray-100">
          {visibleDescriptors.map((descriptor) => {
            const isLocked = isRequiredPermission(descriptor)
            // A required key is on unless the server says it is one of the missing ones.
            const checked = isLocked
              ? !(partner.missingRequired ?? []).includes(descriptor.key)
              : !!toggles[descriptor.key]

            return (
              <div
                key={descriptor.key}
                className="flex items-center justify-between gap-x-3 px-4 py-2 border-b border-gray-50 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-x-2">
                    <span className={`text-[13px] ${isLocked ? 'text-gray-400' : 'text-gray-600'}`}>
                      {permissionLabel(descriptor, i18n)}
                    </span>
                    {isLocked && (
                      <span className="text-[9px] font-semibold text-gray-300 uppercase tracking-wider bg-gray-100 px-1.5 py-0.5">
                        Required
                      </span>
                    )}
                  </div>
                  {descriptor.description && (
                    <p className="text-[11px] text-gray-400 leading-snug">{descriptor.description}</p>
                  )}
                </div>
                <Toggle
                  checked={checked}
                  disabled={isLocked}
                  onChange={isLocked ? undefined : () => onToggle(descriptor.key)}
                />
              </div>
            )
          })}
        </div>

        {/* Actions */}
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
          {!confirmingRevoke ? (
            <>
              <button
                onClick={() => setConfirmingRevoke(true)}
                disabled={isRevoking}
                className="text-[11px] font-medium text-red-400 hover:text-red-500 border border-red-200 hover:border-red-300 hover:bg-red-50 px-3 py-1.5 transition-all disabled:opacity-50"
              >
                Revoke access
              </button>
              <div
                className={`transition-all duration-200 ${hasChanges ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              >
                <button
                  onClick={onSave}
                  disabled={isSaving || isRevoking}
                  className="text-[11px] font-semibold text-white bg-primary-500 hover:bg-primary-600 active:bg-primary-700 px-4 py-1.5 transition-colors disabled:opacity-50"
                >
                  {isSaving || isRevoking ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between w-full gap-x-3">
              <span className="text-[11px] text-gray-400">
                Remove all access for <span className="font-medium text-gray-500">{partner.partnerName}</span>?
              </span>
              <div className="flex gap-x-1.5 flex-shrink-0">
                <button
                  onClick={() => setConfirmingRevoke(false)}
                  className="text-[11px] font-medium text-gray-400 hover:text-gray-600 border border-gray-200 hover:border-gray-300 px-2.5 py-1 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={onRevoke}
                  disabled={isRevoking}
                  className="text-[11px] font-semibold text-white bg-red-500 hover:bg-red-600 active:bg-red-700 px-2.5 py-1 transition-all disabled:opacity-50"
                >
                  {isRevoking ? 'Revoking...' : 'Confirm'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
